import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { useGraphQLClient } from "../graphql/client";
import { productTagsByIdsQuery } from "../graphql/product-gql";
import type { ICaaOrder } from "../interface/customer-account";
import type { IProductTagsByIdsQuery } from "../interface/product";
import { getCaaLineProductIds } from "../utils/order";

/**
 * CAA 라인엔 상품 태그가 없어, 라인의 productId로 Storefront API에서 태그만 조회 (프리오더 판별용)
 * 같은 상품 조합은 캐시를 공유해서 주문 목록의 여러 카드에서 불러도 중복 요청이 나가지 않음
 *
 * isFetched: 조회가 끝났거나(실패 포함) 조회할 상품이 없으면 true — 태그가 오기 전 일반 주문으로 잘못 판별해 상태가 바뀌어 보이는 걸 막는 용도
 */
export default function useCaaProductTags(order?: ICaaOrder | null) {
  const request = useGraphQLClient();

  const productIds = useMemo(() => getCaaLineProductIds(order).sort(), [order]);
  const enabled = productIds.length > 0;

  const { data, isFetched } = useQuery({
    queryKey: ["shopifyV2", "caa-product-tags", productIds],
    queryFn: () => request<IProductTagsByIdsQuery>(productTagsByIdsQuery, { ids: productIds }),
    enabled,
    staleTime: Infinity,
  });

  const tagsByProductId = useMemo(
    () =>
      new Map(
        (data?.nodes ?? [])
          .filter((node): node is IProductTagsByIdsQuery["nodes"][number] & object => Boolean(node))
          .map((node) => [node.id, node.tags]),
      ),
    [data],
  );

  return { tagsByProductId, isFetched: !enabled || isFetched };
}
